/**
 * `<NoteHistoryPanel>` — per-note version history (note-history epic Track 2, 2026-06-22).
 *
 * Lists a note's saved revisions (newest first) with who/what made each change, lets the user
 * preview a revision's full body, and restore it. Restore is NON-destructive server-side (the
 * current state is snapshotted first), so a restore can itself be undone from this same panel.
 *
 * After a successful restore we reload so the editor reliably reflects the restored body — a
 * deliberate, infrequent action where a clean reload beats fighting the editor's content-sync
 * (the restored note is already saved server-side, so nothing is lost).
 */

import { useEffect, useState } from 'react';
import { Modal } from '@/components';
import {
  listNoteRevisions,
  getNoteRevision,
  restoreNoteRevision,
  type NoteRevisionItem,
  type NoteRevisionDetail,
} from '@/api';
import { clearPersistedQueryCache } from '@/providers/queryPersist';
import type { NoteId } from '@/types';

interface NoteHistoryPanelProps {
  noteId: NoteId;
  onClose: () => void;
}

function fmt(ts: string | null | undefined): string {
  if (ts === null || ts === undefined || ts === '') return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Friendly label for the actor who made an edit (the agent-vs-you distinction). */
function actorLabel(actor: string): string {
  switch (actor) {
    case 'mcp':
      return 'AI agent (MCP)';
    case 'rest':
      return 'You (app)';
    case 'router':
      return 'Autorouter';
    case 'chat':
      return 'Chat';
    default:
      return actor || 'System';
  }
}

/** Friendly label for which tool made the change. */
function changeLabel(kind: string): string {
  const map: Record<string, string> = {
    update_note: 'edited',
    append_to_note: 'appended',
    replace_in_note: 'replaced text',
    insert_into_note: 'inserted text',
    restore_note_revision: 'restored a version',
    create_note: 'created',
  };
  return map[kind] ?? kind.replace(/_/g, ' ');
}

export function NoteHistoryPanel({ noteId, onClose }: NoteHistoryPanelProps): JSX.Element {
  const [items, setItems] = useState<NoteRevisionItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<NoteRevisionDetail | null>(null);
  const [restoring, setRestoring] = useState(false);

  // Load the revision list on open / note change.
  useEffect(() => {
    let live = true;
    setItems(null);
    setError(null);
    setSelectedId(null);
    setDetail(null);
    listNoteRevisions(noteId)
      .then((resp) => {
        if (live) setItems(resp.data.items);
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      live = false;
    };
  }, [noteId]);

  // Load the full body of the selected revision for preview.
  useEffect(() => {
    if (selectedId === null) {
      setDetail(null);
      return;
    }
    let live = true;
    setDetail(null);
    getNoteRevision(noteId, selectedId)
      .then((resp) => {
        if (live) setDetail(resp.data);
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      live = false;
    };
  }, [noteId, selectedId]);

  const onRestore = async (): Promise<void> => {
    if (selectedId === null || restoring) return;
    setRestoring(true);
    try {
      await restoreNoteRevision(noteId, selectedId);
      // The restored body is now saved server-side; reload so the editor shows it cleanly.
      // But first drop the persisted (IndexedDB) query cache: the `note` key is whitelisted for
      // persistence and note bodies carry a 5-min staleTime, so a bare reload would rehydrate the
      // PRE-restore body from disk, treat it as fresh, and never refetch — the restore would look
      // like a no-op. Clearing it forces the reload to cold-fetch the restored body.
      await clearPersistedQueryCache();
      window.location.reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      setRestoring(false);
    }
  };

  return (
    <Modal
      eyebrow="Version history"
      title="Note history"
      size="lg"
      onClose={onClose}
      footMeta={
        items === null ? '' : `${items.length} version${items.length === 1 ? '' : 's'} · last 100 kept`
      }
      footActions={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={restoring}>
            Close
          </button>
          <button
            type="button"
            className="btn btn--cool"
            disabled={selectedId === null || restoring}
            onClick={() => void onRestore()}
            title={
              selectedId === null
                ? 'Select a version on the left to restore it'
                : 'Restore this version (non-destructive — the current state is saved first)'
            }
          >
            {restoring ? 'Restoring…' : 'Restore this version'}
          </button>
        </>
      }
    >
      {error !== null ? (
        <p className="modal__text" style={{ color: 'var(--bad)' }}>
          Couldn’t load history: {error}
        </p>
      ) : items === null ? (
        <p className="modal__text">Loading…</p>
      ) : items.length === 0 ? (
        <p className="modal__text" style={{ color: 'var(--fg-4)' }}>
          No saved versions yet. A version is captured automatically every time this note is
          edited (by you, the AI, or the autorouter) — they’ll appear here as you make changes.
        </p>
      ) : (
        <div style={{ display: 'flex', gap: '14px', minHeight: '320px', maxHeight: '52vh' }}>
          {/* Left: the revision list */}
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              flex: '0 0 240px',
              overflowY: 'auto',
              borderRight: '1px solid var(--line)',
            }}
          >
            {items.map((rev) => {
              const active = rev.revisionId === selectedId;
              return (
                <li key={rev.revisionId}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(rev.revisionId)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      border: 'none',
                      borderLeft: active
                        ? '3px solid var(--accent)'
                        : '3px solid transparent',
                      background: active ? 'var(--surface-2)' : 'transparent',
                      cursor: 'pointer',
                      color: 'inherit',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: '0.86rem' }}>{fmt(rev.capturedAt)}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--fg-3)' }}>
                      {actorLabel(rev.actor)} · {changeLabel(rev.changeKind)}
                    </div>
                    <div
                      style={{
                        fontSize: '0.74rem',
                        color: 'var(--fg-4)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {rev.preview || '(empty)'}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Right: the selected revision's full body preview */}
          <div style={{ flex: '1 1 auto', overflowY: 'auto', minWidth: 0 }}>
            {selectedId === null ? (
              <p className="modal__text" style={{ color: 'var(--fg-4)' }}>
                Select a version on the left to preview it, then Restore to bring it back.
              </p>
            ) : detail === null ? (
              <p className="modal__text">Loading version…</p>
            ) : (
              <>
                <div style={{ fontSize: '0.8rem', color: 'var(--fg-3)', marginBottom: '6px' }}>
                  {actorLabel(detail.actor)} · {changeLabel(detail.changeKind)} ·{' '}
                  {fmt(detail.capturedAt)} · {detail.body.length} chars
                </div>
                <pre
                  className="mono"
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.82rem',
                    margin: 0,
                    padding: '10px',
                    background: 'var(--surface-2)',
                    borderRadius: '6px',
                  }}
                >
                  {detail.body || '(empty)'}
                </pre>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
